import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Home, LayoutPanelTop } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PageLiveRender } from '@/components/homepage/page-live-render';
import { useSession } from '@/lib/auth';
import { homepageErrorMessage, homepageLayoutQueryOptions } from '@/lib/homepage';
import { hasPermission } from '@/lib/permissions';

export const Route = createFileRoute('/_authenticated/homepage/')({
  component: HomepageEditorPage,
});

/**
 * Live render of the system home page (slug=home, id=page-home).
 *
 * Container/Row/Column rendered as plain CSS-only divs (no toolbars / no
 * dashed borders / no padding) — matches what the public site would render.
 * Each Slot embeds <SlotGroupEditor> so Group Content remains editable via
 * the per-slot edit modal. Page-level save flow is gone because layout
 * structure is no longer editable here.
 *
 * Structural editing (add/remove rows, change column spans, etc.) lives at
 * /pages/page-home/edit (super_admin only). A small "แก้ Layout →" link in
 * the top-right of this page jumps over there when the viewer is
 * super_admin.
 */
function HomepageEditorPage() {
  const { user } = useSession();
  const canManage = hasPermission(user, 'settings.update');
  const layoutQuery = useQuery(homepageLayoutQueryOptions);

  if (!canManage) {
    return (
      <div className="p-8 text-center text-sm text-slate-500">
        ไม่มีสิทธิ์เข้าถึง — ต้องมี settings.update
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              <Home className="text-brand-600 h-6 w-6" />
              หน้าแรกของเว็บ public
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              แก้ไข Group Content ในแต่ละ slot ได้จากที่นี่ — โครงสร้าง layout จัดการที่{' '}
              <Link to="/pages" className="underline">
                /pages
              </Link>{' '}
              (super_admin)
            </p>
          </div>
          {user?.role.slug === 'super_admin' && (
            <Link
              to="/pages/$id/edit"
              params={{ id: 'page-home' }}
              className="inline-flex items-center gap-1 rounded border border-slate-300 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              <LayoutPanelTop className="h-3.5 w-3.5" />
              แก้ Layout →
            </Link>
          )}
        </div>

        {layoutQuery.isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{homepageErrorMessage(layoutQuery.error)}</AlertDescription>
          </Alert>
        )}

        {layoutQuery.isLoading || !layoutQuery.data ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900">
            กำลังโหลด layout...
          </div>
        ) : (
          <PageLiveRender layout={layoutQuery.data} />
        )}
      </div>
    </div>
  );
}
